import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #loaded = false;
  #observer;
  #fallbackTimer;

  connectedCallback() {
    super.connectedCallback();

    const strategy = this.dataset.videoLoadStrategy || 'viewport';
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (strategy === 'priority' && !isMobile) {
      this.#loadVideo();
      return;
    }

    if (strategy === 'priority') {
      this.#bindInteractionLoad();
      this.#fallbackTimer = window.setTimeout(this.#scheduleIdleLoad, 10000);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.#loadVideo();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#loadVideo();
      },
      { rootMargin: '300px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    window.clearTimeout(this.#fallbackTimer);
    this.#removeInteractionLoad();
  }

  #bindInteractionLoad() {
    window.addEventListener('pointerdown', this.#loadVideo, { once: true });
    window.addEventListener('touchstart', this.#loadVideo, { passive: true, once: true });
    window.addEventListener('wheel', this.#loadVideo, { passive: true, once: true });
    window.addEventListener('keydown', this.#loadVideo, { once: true });
  }

  #removeInteractionLoad() {
    window.removeEventListener('pointerdown', this.#loadVideo);
    window.removeEventListener('touchstart', this.#loadVideo);
    window.removeEventListener('wheel', this.#loadVideo);
    window.removeEventListener('keydown', this.#loadVideo);
  }

  #scheduleIdleLoad = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(this.#loadVideo, { timeout: 2000 });
    } else {
      this.#loadVideo();
    }
  };

  #loadVideo = () => {
    if (this.#loaded) return;
    this.#loaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    videoElement.play().catch(() => {});

    this.#observer?.disconnect();
    window.clearTimeout(this.#fallbackTimer);
    this.#removeInteractionLoad();
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
